import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Lock, Check, Crown, Sparkles, X, Volume2, BookOpen, GraduationCap,
  Mail, MessageCircle, ShieldCheck, AlertTriangle
} from 'lucide-react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { BACKEND_URL } from '../lib/backend';
import { getLmsContent, mergeLmsContent } from '../data/lmsContent';

/**
 * LMSPremiumPaywall — full-screen / modal overlay shown when an anonymous
 * or lead user tries to open a premium phoneme card.
 *
 * The component supports two render modes via the `variant` prop:
 *   - 'modal'      → overlay on top of the underlying page (closable)
 *   - 'fullscreen' → covers the whole viewport (no close button; used as
 *                    a route-level guard inside PhonemeCardPage)
 *
 * The CTA is intentionally soft: we collect an interested-prospect email
 * via POST /api/lms/interest (a free, non-Stripe lead capture for now).
 * Stripe wiring can be added later without changing this component.
 */

const BENEFIT_ICONS = [BookOpen, Volume2, GraduationCap, Sparkles];

// Fallback tiers — used ONLY if the content-store fetch fails, so the paywall
// always renders. The live source of truth is GET /api/lms/pricing (editable
// from the admin panel without a redeploy).
const DEFAULT_TIERS = [
  {
    id: 'monthly',
    label: 'Mensile',
    price: '€19',
    period: '/ mese',
    sub: 'Rinnovo automatico, disdici quando vuoi',
    cta: 'Inizia',
    highlight: false,
    badge: '',
  },
  {
    id: 'annual',
    label: 'Annuale',
    price: '€149',
    period: '/ anno',
    sub: 'Equivalente a €12,40/mese · 35% di sconto',
    cta: 'Risparmia 35%',
    highlight: true,
    badge: 'Più scelto',
  },
];

const LMSPremiumPaywall = ({
  cardId = '',
  cardLabel = '',
  variant = 'modal',
  onClose,
  showLoginLink = true,
}) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [name, setName]   = useState('');
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState('idle'); // idle | paid | error
  const [errorMsg, setErrorMsg] = useState('');
  const [payMsg, setPayMsg] = useState(''); // cancel / payment error notice
  const [selectedTier, setSelectedTier] = useState('annual');
  // Content-store driven: pricing tiers + PayPal client-id + published-card
  // count + publish/hide toggle. All from the backend so numbers stay in sync
  // with the Library and can be edited (or the paywall killed) without redeploy.
  const [tiers, setTiers] = useState(DEFAULT_TIERS);
  const [clientId, setClientId] = useState('');
  const [paywallEnabled, setPaywallEnabled] = useState(true);
  const [publishedCount, setPublishedCount] = useState(null);
  // Editable copy (content store) — defaults until GET /api/lms/copy resolves.
  const [copy, setCopy] = useState(() => getLmsContent('it').paywall);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [pricingRes, phonemesRes, copyRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/lms/pricing`),
          fetch(`${BACKEND_URL}/api/phonemes`),
          fetch(`${BACKEND_URL}/api/lms/copy`),
        ]);
        if (pricingRes.ok) {
          const data = await pricingRes.json();
          if (!cancelled) {
            if (Array.isArray(data.tiers) && data.tiers.length) {
              setTiers(data.tiers);
              if (!data.tiers.some((t) => t.id === 'annual')) {
                setSelectedTier(data.tiers[data.tiers.length - 1].id);
              }
            }
            if (data.client_id) setClientId(data.client_id);
            setPaywallEnabled(data.paywall_enabled !== false);
          }
        }
        if (phonemesRes.ok) {
          const list = await phonemesRes.json();
          if (!cancelled && Array.isArray(list)) setPublishedCount(list.length);
        }
        if (copyRes.ok) {
          const override = await copyRes.json();
          if (!cancelled) setCopy(mergeLmsContent(override, 'it').paywall);
        }
      } catch { /* keep defaults; paywall must always render */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const selected = tiers.find((t) => t.id === selectedTier) || tiers[0];
  const cleanEmail = (email || '').trim();
  const emailValid = /^[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}$/.test(cleanEmail);
  const formValid = emailValid && consent;

  // {N}/{piano}/{email} placeholder substitution for the editable copy.
  const nStr = publishedCount ? String(publishedCount) : '';
  const fill = (s = '') => (s || '')
    .replace(/\{N\}/g, nStr)
    .replace(/\{piano\}/g, selected?.label || '')
    .replace(/\{email\}/g, cleanEmail);

  // Consent copy comes from the content store (kept in sync with the audit).
  const CONSENT_VERSION = 'paywall-1.0';
  const CONSENT_TEXT = copy.consent;

  // Lock scroll while modal is open
  useEffect(() => {
    if (variant !== 'modal') return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [variant]);

  // Persist the lead BEFORE opening PayPal → we keep the contact even if the
  // payment is cancelled or fails. Throws on failure to abort checkout.
  const saveLead = async () => {
    setErrorMsg(''); setPayMsg('');
    const res = await fetch(`${BACKEND_URL}/api/lms/paywall/lead`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: cleanEmail,
        name: (name || '').trim() || null,
        card_id: cardId || null,
        tier_id: selected?.id || selectedTier,
        plan_id: selected?.plan_id || '',
        consent_privacy: consent,
        consent_version: CONSENT_VERSION,
        consent_text: CONSENT_TEXT,
      }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.detail || 'Salvataggio contatto non riuscito');
    }
  };

  // onApprove → persist the subscriptionID for MANUAL activation.
  const saveSubscription = async (subscriptionID) => {
    await fetch(`${BACKEND_URL}/api/lms/paywall/subscription`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        subscription_id: subscriptionID,
        plan_id: selected?.plan_id || '',
        tier_id: selected?.id || selectedTier,
        email: cleanEmail,
        name: (name || '').trim() || null,
      }),
    });
  };

  const containerClass = variant === 'fullscreen'
    ? 'fixed inset-0 z-[60] overflow-y-auto bg-[#04070d]'
    : 'fixed inset-0 z-[60] overflow-y-auto bg-slate-950/85 backdrop-blur-sm';

  return (
    <div
      className={containerClass}
      role="dialog"
      aria-modal="true"
      data-testid="lms-paywall"
    >
      <style>{`
        @keyframes paywall-enter {
          from { opacity:0; transform: translateY(24px) scale(.97); }
          to   { opacity:1; transform: none; }
        }
        .paywall-enter { animation: paywall-enter .45s cubic-bezier(.2,.8,.2,1) both; }
        @keyframes paywall-aura {
          0%,100% { box-shadow: 0 0 0 0 rgba(251,146,60,0); }
          50%     { box-shadow: 0 0 60px 0 rgba(251,146,60,0.20); }
        }
        .paywall-aura { animation: paywall-aura 4.5s ease-in-out infinite; }
      `}</style>

      <div className="min-h-full flex items-center justify-center p-4 md:p-8">
        <div className="paywall-enter relative w-full max-w-4xl bg-gradient-to-br from-slate-900 via-slate-900 to-slate-950 border border-orange-500/30 rounded-3xl shadow-2xl overflow-hidden paywall-aura">
          {/* Ambient gradient */}
          <div className="absolute inset-0 pointer-events-none opacity-60"
               style={{ background: 'radial-gradient(circle at 100% 0%, rgba(251,146,60,0.18) 0%, transparent 55%), radial-gradient(circle at 0% 100%, rgba(34,211,238,0.12) 0%, transparent 50%)' }} />

          {variant === 'modal' && onClose && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Chiudi"
              className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-slate-800/80 hover:bg-slate-700 text-slate-300 hover:text-white flex items-center justify-center transition-colors"
              data-testid="lms-paywall-close"
            >
              <X className="w-4 h-4" />
            </button>
          )}

          <div className="relative p-7 md:p-10 lg:p-12">
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center shadow-lg">
                <Lock className="w-6 h-6 text-slate-900" />
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-orange-300">
                  {copy.badge}
                </p>
                {cardLabel && (
                  <p className="text-sm text-slate-300">
                    Scheda <span className="font-bold text-white">{cardLabel}</span>
                  </p>
                )}
              </div>
            </div>

            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white leading-tight mb-3" data-testid="lms-paywall-title">
              {copy.title}
            </h2>
            <p className="text-slate-400 text-base md:text-lg mb-8 max-w-2xl" data-testid="lms-paywall-subtitle">
              {fill(copy.subtitle)}
            </p>

            {/* Benefits grid */}
            <div className="grid sm:grid-cols-2 gap-3 mb-8" data-testid="lms-paywall-benefits">
              {(copy.benefits || []).map((b, i) => {
                const Icon = BENEFIT_ICONS[i] || BookOpen;
                return (
                  <div key={i} className="flex items-start gap-3 p-4 rounded-xl bg-slate-800/50 border border-slate-700/50">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500/20 to-orange-500/20 border border-cyan-500/30 flex items-center justify-center shrink-0">
                      <Icon className="w-5 h-5 text-cyan-300" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-white font-bold text-sm mb-0.5" data-testid={i === 0 ? 'lms-paywall-card-count' : undefined}>
                        {fill(b.title)}
                      </p>
                      <p className="text-slate-400 text-xs leading-relaxed">{fill(b.desc)}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Publish/hide kill switch — if disabled, hide payment UI */}
            {!paywallEnabled ? (
              <div className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-6 flex items-start gap-3" data-testid="lms-paywall-disabled">
                <div className="w-10 h-10 rounded-full bg-amber-500/20 border border-amber-400/50 flex items-center justify-center shrink-0">
                  <AlertTriangle className="w-5 h-5 text-amber-300" />
                </div>
                <div>
                  <p className="font-bold text-amber-100">{copy.disabledHeader}</p>
                  <p className="text-sm text-amber-100/80 mt-1">
                    {copy.disabledBody}
                  </p>
                </div>
              </div>
            ) : status === 'paid' ? (
              <div className="rounded-2xl border border-emerald-500/40 bg-emerald-500/10 p-5 flex items-start gap-3" data-testid="lms-paywall-success">
                <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center shrink-0">
                  <Check className="w-5 h-5 text-slate-900" strokeWidth={3} />
                </div>
                <div>
                  <p className="font-bold text-emerald-200">{copy.success?.header}</p>
                  <p className="text-sm text-emerald-100/80 mt-1 whitespace-pre-line" data-testid="lms-paywall-success-body">
                    {fill(copy.success?.body)}
                  </p>
                  {variant === 'modal' && onClose && (
                    <button type="button" onClick={onClose} className="mt-3 text-xs font-bold text-emerald-300 hover:text-white underline underline-offset-2" data-testid="lms-paywall-success-close">
                      Chiudi
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <>
                {/* Tier selector */}
                <div className="grid md:grid-cols-2 gap-3 mb-6" data-testid="lms-paywall-tiers">
                  {tiers.map((t) => {
                    const active = selectedTier === t.id;
                    return (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => { setSelectedTier(t.id); setPayMsg(''); }}
                        data-testid={`lms-paywall-tier-${t.id}`}
                        className={`relative text-left p-5 rounded-2xl border-2 transition-all duration-300 ${
                          active
                            ? 'border-orange-400 bg-orange-500/10 shadow-[0_0_30px_rgba(251,146,60,0.18)]'
                            : 'border-slate-700 bg-slate-900/40 hover:border-slate-500'
                        }`}
                      >
                        {t.badge && (
                          <span className="absolute -top-2.5 right-4 inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-gradient-to-r from-orange-500 to-amber-500 text-slate-900 text-[10px] font-bold uppercase tracking-wider shadow-lg">
                            <Crown className="w-3 h-3" /> {t.badge}
                          </span>
                        )}
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <p className={`text-xs font-bold uppercase tracking-widest ${active ? 'text-orange-300' : 'text-slate-400'}`}>{t.label}</p>
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${active ? 'border-orange-400 bg-orange-400' : 'border-slate-600'}`}>
                            {active && <Check className="w-3 h-3 text-slate-900" strokeWidth={4} />}
                          </div>
                        </div>
                        <p className="text-3xl font-black text-white leading-none">
                          {t.price}<span className="text-base font-semibold text-slate-400 ml-1">{t.period}</span>
                        </p>
                        <p className="text-xs text-slate-500 mt-1.5">{t.sub}</p>
                      </button>
                    );
                  })}
                </div>

                {/* Contact fields (email required before payment) */}
                <div className="grid md:grid-cols-2 gap-3 mb-3" data-testid="lms-paywall-form">
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Il tuo nome (facoltativo)"
                    maxLength={120}
                    className="w-full px-4 py-3 rounded-xl border border-slate-700 bg-slate-900/60 text-white placeholder:text-slate-500 focus:border-orange-400 focus:ring-2 focus:ring-orange-400/30 outline-none transition-all"
                    data-testid="lms-paywall-name-input"
                  />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="email@dominio.com"
                    maxLength={180}
                    className="w-full px-4 py-3 rounded-xl border border-slate-700 bg-slate-900/60 text-white placeholder:text-slate-500 focus:border-orange-400 focus:ring-2 focus:ring-orange-400/30 outline-none transition-all"
                    data-testid="lms-paywall-email-input"
                  />
                </div>

                {/* Privacy consent — unchecked by default + link to policy */}
                <label className="flex items-start gap-2.5 mb-4 cursor-pointer select-none" data-testid="lms-paywall-consent-label">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    className="mt-0.5 w-4 h-4 accent-orange-500 shrink-0"
                    data-testid="lms-paywall-consent-checkbox"
                  />
                  <span className="text-xs text-slate-400 leading-relaxed">
                    Acconsento al trattamento dei miei dati personali secondo l&rsquo;
                    <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-cyan-300 hover:text-white underline underline-offset-2" data-testid="lms-paywall-privacy-link">
                      Informativa sulla Privacy
                    </a>.
                  </span>
                </label>

                {payMsg && (
                  <div className="text-sm text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 mb-3" data-testid="lms-paywall-pay-notice">{payMsg}</div>
                )}
                {status === 'error' && errorMsg && (
                  <div className="text-sm text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2 mb-3" data-testid="lms-paywall-error">{errorMsg}</div>
                )}

                {/* Gate hint shown until email + consent provided */}
                {!formValid && (
                  <p className="text-xs text-slate-500 mb-2 flex items-center gap-1.5" data-testid="lms-paywall-gate-hint">
                    <ShieldCheck className="w-3.5 h-3.5" />
                    {copy.gateHint}
                  </p>
                )}

                {/* CTA label above the PayPal button (button text follows the plan) */}
                {formValid && (
                  <p className="text-sm font-bold text-orange-300 mb-2" data-testid="lms-paywall-cta-label">
                    {fill(copy.ctaTemplate)}
                  </p>
                )}

                {/* PayPal Subscriptions — single button, plan_id follows the
                    selected tier; re-renders on tier / validity change. */}
                <div className="min-h-[52px]" data-testid="lms-paywall-paypal">
                  {clientId ? (
                    <PayPalScriptProvider
                      options={{ 'client-id': clientId, vault: true, intent: 'subscription' }}
                    >
                      <PayPalButtons
                        style={{ layout: 'horizontal', color: 'gold', shape: 'pill', label: 'subscribe', height: 48 }}
                        disabled={!formValid}
                        forceReRender={[selected?.plan_id, formValid]}
                        createSubscription={async (data, actions) => {
                          await saveLead(); // contact saved before checkout opens
                          return actions.subscription.create({ plan_id: selected?.plan_id });
                        }}
                        onApprove={async (data) => {
                          try {
                            await saveSubscription(data.subscriptionID);
                          } catch { /* lead already saved; still show success */ }
                          setStatus('paid');
                        }}
                        onCancel={() => setPayMsg(copy.cancel)}
                        onError={() => setPayMsg(copy.error)}
                      />
                    </PayPalScriptProvider>
                  ) : (
                    <div className="text-xs text-slate-500" data-testid="lms-paywall-paypal-unconfigured">Caricamento del metodo di pagamento…</div>
                  )}
                </div>
              </>
            )}

            {/* Bottom row: login link + customer-care */}
            <div className="mt-6 pt-6 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
              {showLoginLink && (
                <button
                  type="button"
                  onClick={() => { onClose?.(); navigate('/login'); }}
                  className="text-cyan-300 hover:text-white font-semibold transition-colors"
                  data-testid="lms-paywall-login-link"
                >
                  {copy.loginLink}
                </button>
              )}
              <div className="flex items-center gap-4 text-slate-500">
                <a href="mailto:admissions@vocalfitness.org" className="inline-flex items-center gap-1.5 hover:text-cyan-300 transition-colors">
                  <Mail className="w-3.5 h-3.5" /> admissions@vocalfitness.org
                </a>
                <a href="https://wa.me/393515765749" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 hover:text-emerald-300 transition-colors">
                  <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LMSPremiumPaywall;
